import { useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../utils/cn'
import { useIsMobile } from '../hooks/useIsMobile'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { 
  Database, 
  Shield, 
  Bell, 
  Palette, 
  Globe, 
  Save,
  RefreshCw,
  Key,
  Server,
  Network,
  Settings
} from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { SectionHeader } from '../components/ui/SectionHeader'

const labelClass = 'block text-xs font-medium text-glass-400 mb-1.5'
const helpClass = 'text-xs text-glass-500 mt-1'

/** Toggle switch built on the shared `.switch` primitive; input props are passed through untouched. */
const Switch = (props: InputHTMLAttributes<HTMLInputElement>) => (
  <label className="switch">
    <input type="checkbox" {...props} />
    <span />
  </label>
)

/** A titled setting row with its control on the right (inset group inside a card). */
const SettingRow = ({ title, description, children, className }: { title: string; description: string; children: ReactNode; className?: string }) => (
  <div className={cn('subcard flex items-center justify-between gap-4 p-3', className)}>
    <div className="min-w-0">
      <h4 className="text-sm font-medium text-white">{title}</h4>
      <p className="text-xs text-glass-400">{description}</p>
    </div>
    {children}
  </div>
)

export function SettingsView() {
  const isMobile = useIsMobile()
  const { theme, setTheme, galaxyEnabled, setGalaxyEnabled } = useTheme()
  const { identity } = useAuth()
  const { securityAccessibilityMode, isLoading: preferencesLoading, isSaving: preferencesSaving, error: preferencesError, setSecurityAccessibilityMode, retry: retryPreferences } = useUserPreferences()
  const [activeTab, setActiveTab] = useState('general')
  const [settings, setSettings] = useState({
    refreshInterval: 30,
    theme: 'dark',
    notifications: true,
    autoBackup: true,
    backupInterval: 24,
    maxConnections: 100,
    timeout: 5000,
    retryAttempts: 3
  })

  const tabs = [
    { id: 'general', label: 'General', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'advanced', label: 'Advanced', icon: Server }
  ]

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const saveSettings = () => {
    // In a real implementation, this would save to localStorage or backend
    console.log('Saving settings:', settings)
    // Show success message
  }

  const resetSettings = () => {
    // Reset to defaults
    setSettings({
      refreshInterval: 30,
      theme: 'dark',
      notifications: true,
      autoBackup: true,
      backupInterval: 24,
      maxConnections: 100,
      timeout: 5000,
      retryAttempts: 3
    })
  }

  const renderGeneral = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Accessibility" icon={Shield} />
        <label className="subcard flex min-h-11 cursor-pointer items-center justify-between gap-4 p-3" htmlFor="security-accessibility-mode">
          <span className="min-w-0">
            <span className="block text-sm font-medium text-white">Security accessibility mode</span>
            <span id="security-accessibility-description" className="block text-xs text-glass-400">
              Use horizontal lines instead of security dots, with a still background. Screenshot attribution stays enabled in both modes.
            </span>
          </span>
          <span className="switch">
            <input
              id="security-accessibility-mode"
              type="checkbox"
              role="switch"
              aria-label="Security accessibility mode"
              aria-describedby="security-accessibility-description security-accessibility-status"
              checked={securityAccessibilityMode}
              disabled={!identity || preferencesLoading || preferencesSaving}
              onChange={event => void setSecurityAccessibilityMode(event.target.checked)}
            />
            <span />
          </span>
        </label>
        <p id="security-accessibility-status" role="status" className="mt-3 text-xs text-glass-400">
          {!identity ? 'Sign in to save this preference to your account.'
            : preferencesSaving ? 'Saving to your account...'
            : preferencesError ? 'This selection is active here, but saving has not been confirmed. Retry to save it to your account.'
            : `Saved to your account. ${securityAccessibilityMode ? 'Horizontal lines' : 'Security dots'} are enabled. Changes save automatically.`}
        </p>
        {preferencesError && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p role="alert" className="text-xs text-[var(--tone-danger-text)]">{preferencesError}</p>
            <button type="button" className="glass-button glass-button-sm" disabled={preferencesSaving} onClick={() => void retryPreferences()}>
              Retry saving preference
            </button>
          </div>
        )}
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Redis Connection" icon={Database} />
        <div className="space-y-4">
          <p className={helpClass}>
            Game Redis is read through the Argus backend over its private tunnel, using your
            Cloudflare Access session. There is no browser-side connection or key to configure.
          </p>

          <div>
            <label className={labelClass}>
              Refresh Interval (seconds)
            </label>
            <input
              type="number"
              value={settings.refreshInterval}
              onChange={(e) => handleSettingChange('refreshInterval', parseInt(e.target.value))}
              className="glass-input w-full tabular-nums"
              min="5"
              max="300"
            />
            <p className={helpClass}>
              How often to refresh data from Redis
            </p>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Performance" icon={Server} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Max Connections
            </label>
            <input
              type="number"
              value={settings.maxConnections}
              onChange={(e) => handleSettingChange('maxConnections', parseInt(e.target.value))}
              className="glass-input w-full tabular-nums"
              min="10"
              max="1000"
            />
          </div>
          
          <div>
            <label className={labelClass}>
              Timeout (ms)
            </label>
            <input
              type="number"
              value={settings.timeout}
              onChange={(e) => handleSettingChange('timeout', parseInt(e.target.value))}
              className="glass-input w-full tabular-nums"
              min="1000"
              max="30000"
            />
          </div>
          
          <div>
            <label className={labelClass}>
              Retry Attempts
            </label>
            <input
              type="number"
              value={settings.retryAttempts}
              onChange={(e) => handleSettingChange('retryAttempts', parseInt(e.target.value))}
              className="glass-input w-full tabular-nums"
              min="1"
              max="10"
            />
          </div>
        </div>
      </div>
    </div>
  )

  const renderSecurity = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Access Control" icon={Shield} />
        <div className="space-y-3">
          <SettingRow title="Two-Factor Authentication" description="Require 2FA for admin access">
            <Switch aria-label="Two-Factor Authentication" />
          </SettingRow>
          
          <SettingRow title="Session Timeout" description="Auto-logout after inactivity">
            <select className="glass-select w-36 shrink-0" aria-label="Session Timeout">
              <option>15 minutes</option>
              <option>30 minutes</option>
              <option>1 hour</option>
              <option>4 hours</option>
              <option>Never</option>
            </select>
          </SettingRow>
          
          <SettingRow title="IP Whitelist" description="Restrict access to specific IPs">
            <Switch aria-label="IP Whitelist" />
          </SettingRow>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="API Security" icon={Key} />
        <div className="space-y-4">
          <div>
            <label className={labelClass}>
              Rate Limiting
            </label>
            <select className="glass-select w-full">
              <option>100 requests per minute</option>
              <option>500 requests per minute</option>
              <option>1000 requests per minute</option>
              <option>Unlimited</option>
            </select>
          </div>
          
          <div>
            <label className={labelClass}>
              CORS Origins
            </label>
            <textarea
              className="glass-input w-full min-h-[5rem]"
              placeholder="https://yourdomain.com
https://admin.yourdomain.com"
            />
            <p className={helpClass}>
              One origin per line
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderNotifications = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Notification Preferences" icon={Bell} />
        <div className="space-y-3">
          <SettingRow title="System Alerts" description="Critical system notifications">
            <Switch defaultChecked aria-label="System Alerts" />
          </SettingRow>
          
          <SettingRow title="Player Activity" description="Player join/leave notifications">
            <Switch aria-label="Player Activity" />
          </SettingRow>
          
          <SettingRow title="Permission Changes" description="Admin permission updates">
            <Switch defaultChecked aria-label="Permission Changes" />
          </SettingRow>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Backup & Maintenance" icon={Network} />
        <div className="space-y-4">
          <SettingRow title="Auto Backup" description="Automatically backup Redis data">
            <Switch 
              checked={settings.autoBackup}
              onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
              aria-label="Auto Backup"
            />
          </SettingRow>
          
          {settings.autoBackup && (
            <div>
              <label className={labelClass}>
                Backup Interval (hours)
              </label>
              <input
                type="number"
                value={settings.backupInterval}
                onChange={(e) => handleSettingChange('backupInterval', parseInt(e.target.value))}
                className="glass-input w-full tabular-nums"
                min="1"
                max="168"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderAppearance = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Theme & Colors" icon={Palette} />
        <div className="space-y-4">
          <div>
            <label className={labelClass}>
              Theme
            </label>
            <select 
              value={theme}
              onChange={(e) => setTheme(e.target.value === 'light' ? 'light' : 'dark')}
              className="glass-select w-full"
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </select>
            <p className={helpClass}>
              Switch between the dark cosmic theme and the light theme.
            </p>
          </div>

          <SettingRow
            title="Animated Galaxy Background"
            description={securityAccessibilityMode && galaxyEnabled
              ? 'Paused by Security accessibility mode. This background resumes when the mode is off (dark theme, desktop only).'
              : 'Show the 3D galaxy animation (dark theme, desktop only)'}
          >
            <Switch
              checked={galaxyEnabled}
              onChange={(e) => setGalaxyEnabled(e.target.checked)}
              aria-label="Animated Galaxy Background"
            />
          </SettingRow>
          
          <div>
            <label className={labelClass}>
              Accent Color
            </label>
            <div className="flex flex-wrap gap-2">
              {['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'].map((color) => (
                <button
                  key={color}
                  type="button"
                  className="w-9 h-9 rounded-lg border border-glass-200/25 hover:border-glass-200/60 shadow-control"
                  style={{ backgroundColor: color }}
                  title={color}
                  aria-label={`Accent color ${color}`}
                />
              ))}
            </div>
          </div>
          
          <div>
            <label className={labelClass}>
              Glassmorphism Intensity
            </label>
            <input
              type="range"
              min="0"
              max="100"
              defaultValue="70"
              className="w-full h-2 bg-glass-200/20 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-glass-500 mt-1">
              <span>Subtle</span>
              <span>Intense</span>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Localization" icon={Globe} />
        <div className="space-y-4">
          <div>
            <label className={labelClass}>
              Language
            </label>
            <select className="glass-select w-full">
              <option>English</option>
              <option>Spanish</option>
              <option>French</option>
              <option>German</option>
              <option>Chinese</option>
            </select>
          </div>
          
          <div>
            <label className={labelClass}>
              Time Zone
            </label>
            <select className="glass-select w-full">
              <option>UTC</option>
              <option>America/New_York</option>
              <option>America/Los_Angeles</option>
              <option>Europe/London</option>
              <option>Asia/Tokyo</option>
            </select>
          </div>
          
          <div>
            <label className={labelClass}>
              Date Format
            </label>
            <select className="glass-select w-full">
              <option>MM/DD/YYYY</option>
              <option>DD/MM/YYYY</option>
              <option>YYYY-MM-DD</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  )

  const renderAdvanced = () => (
    <div className="space-y-6">
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="System Configuration" icon={Server} />
        <div className="space-y-3">
          <SettingRow title="Debug Mode" description="Enable detailed logging">
            <Switch aria-label="Debug Mode" />
          </SettingRow>
          
          <SettingRow title="Performance Monitoring" description="Track system performance">
            <Switch defaultChecked aria-label="Performance Monitoring" />
          </SettingRow>
          
          <SettingRow title="Auto Updates" description="Automatically update the application">
            <Switch aria-label="Auto Updates" />
          </SettingRow>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Danger Zone" icon={Database} iconClassName="text-red-400" />
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-danger max-md:w-full">
            Clear All Data
          </button>
          <button type="button" className="glass-button max-md:w-full">
            Reset to Defaults
          </button>
          <button type="button" className="glass-button max-md:w-full">
            Export Configuration
          </button>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneral()
      case 'security':
        return renderSecurity()
      case 'notifications':
        return renderNotifications()
      case 'appearance':
        return renderAppearance()
      case 'advanced':
        return renderAdvanced()
      default:
        return renderGeneral()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="System"
        title="Settings"
        description="Configure your ARGUS preferences and system settings"
        icon={Settings}
        actions={
          <>
            <button
              type="button"
              onClick={resetSettings}
              className="glass-button"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              type="button"
              onClick={saveSettings}
              className="btn-primary"
            >
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          </>
        }
      >
        {/* Tabs */}
        {isMobile ? (
          <select
            value={activeTab}
            onChange={e => setActiveTab(e.target.value)}
            className="glass-select w-full"
            aria-label="Settings section"
          >
            {tabs.map(tab => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
          </select>
        ) : (
          <div className="segmented" role="tablist" aria-label="Settings sections">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-active={isActive ? 'true' : 'false'}
                  onClick={() => setActiveTab(tab.id)}
                  className="segmented-item"
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </PageHeader>

      {/* Tab Content */}
      <div className="animate-slide-up">
        {renderTabContent()}
      </div>
    </div>
  )
}
